/**
 * Event Hub authorization, as the frontend sees it: which LDAP groups admit a user
 * to the `/event/*` surface, and which capabilities each role carries.
 *
 * This mirrors the service's own model one-for-one so the two cannot disagree —
 * see `AWSECEventHub`:
 *   - groups → roles: `auth/RoleResolver.java` (`GROUP_TO_ROLE`)
 *   - roles → operations: `auth/RouteRegistry.java`
 *   - the roles themselves: `auth/Role.java`
 * `doc/authz-model.md` describes the design, but it is a DRAFT and has already
 * drifted from the code (it lists `PUT /events/{id}`, the code uses `PATCH`; it
 * predates publish/unpublish). Where they differ, the code above wins.
 *
 * The frontend gate is UX only — the authoritative check is the service's Layer-2
 * authorizer. This module exists so the frontend does not show a button that will
 * 403, nor hide one the service would have allowed.
 *
 * Two properties of the service model shape everything here:
 *
 *   1. **A caller holds a SET of roles, not one role.** `RoleResolver` accumulates
 *      a role for every Event Hub group the alias belongs to, so a user can be both
 *      BD and marketer, or both CN and HK marketer. Access is the UNION of what
 *      those roles permit. Hence the capability predicates below take the whole set
 *      and there is deliberately no `primaryRoleOf()` — asking "which one is this
 *      user" has no answer for a dual-role user, and inventing one would give them
 *      an arbitrary UI.
 *
 *   2. **Region is a role, not an attribute.** Mainland and Hong Kong marketers are
 *      separate groups (an explicit decision, `authz-model.md` §8.1). They carry the
 *      same capabilities and differ only in which data the service lets them touch,
 *      so {@link isMarketer} treats them alike; a caller denied by region gets a 403
 *      from the service, which is the only place that scope is known.
 */

// ── Groups ───────────────────────────────────────────────────────────────────

/** Base membership. Read-only access to shared data. */
export const EVENT_HUB_VIEWER_GROUP = 'event-hub';
/** Nominates guests; updates attendance after a nomination is approved. */
export const EVENT_HUB_BD_GROUP = 'event-hub-bd';
/** Mainland-CN marketer. Approves/rejects nominations; manages events. */
export const EVENT_HUB_MARKETER_CN_GROUP = 'event-hub-marketer-cn';
/** Hong Kong marketer. Same capabilities as CN, different data scope. */
export const EVENT_HUB_MARKETER_HK_GROUP = 'event-hub-marketer-hk';
/** Full control. */
export const EVENT_HUB_ADMIN_GROUP = 'event-hub-admin';

// ── Roles ────────────────────────────────────────────────────────────────────

export type EventHubRole =
  | 'VIEWER'
  | 'BD'
  | 'MARKETER_CN'
  | 'MARKETER_HK'
  | 'ADMIN';

/**
 * LDAP group → role. Byte-identical to `RoleResolver.GROUP_TO_ROLE`; keep them in
 * step, because a group missing here silently costs the user their capabilities in
 * the UI while the service still grants them.
 */
const GROUP_TO_ROLE: Readonly<Record<string, EventHubRole>> = {
  [EVENT_HUB_VIEWER_GROUP]: 'VIEWER',
  [EVENT_HUB_BD_GROUP]: 'BD',
  [EVENT_HUB_MARKETER_CN_GROUP]: 'MARKETER_CN',
  [EVENT_HUB_MARKETER_HK_GROUP]: 'MARKETER_HK',
  [EVENT_HUB_ADMIN_GROUP]: 'ADMIN',
};

/** Every group that maps to a role — the admission set. */
export const EVENT_HUB_ACCESS_GROUPS: readonly string[] = Object.keys(GROUP_TO_ROLE);

/**
 * The roles a caller holds, from their LDAP groups. Never null; an empty set means
 * no Event Hub membership at all, which is exactly the service's deny condition
 * (`DefaultRequestAuthorizer`: `roles.isEmpty()` → 403).
 */
export function rolesOf(
  ldapGroups: readonly string[] | undefined,
): ReadonlySet<EventHubRole> {
  const roles = new Set<EventHubRole>();
  for (const group of ldapGroups ?? []) {
    const role = GROUP_TO_ROLE[group];
    if (role) roles.add(role);
  }
  return roles;
}

/**
 * The roles named in a `GetCurrentUser` response.
 *
 * The service sends `auth/Role` names, which are this module's {@link EventHubRole} values. A
 * name this client does not know — a role added server-side first — is dropped rather than
 * trusted: it carries no capability here until a predicate below names it.
 */
export function rolesFromService(
  names: readonly string[] | undefined,
): ReadonlySet<EventHubRole> {
  const known = new Set<string>(Object.values(GROUP_TO_ROLE));
  const roles = new Set<EventHubRole>();
  for (const name of names ?? []) {
    if (known.has(name)) roles.add(name as EventHubRole);
  }
  return roles;
}

// ── Admission ────────────────────────────────────────────────────────────────

/**
 * True when the user may open the Event Hub surface at all.
 *
 * Deliberately "holds any Event Hub group", NOT "holds `event-hub`". The service
 * admits any caller whose resolved role set is non-empty — it does not require the
 * base group — so requiring `event-hub` here would deny a user the service serves,
 * e.g. someone granted only `event-hub-bd`. (`authz-model.md` §4 does claim base
 * membership is required, but `DefaultRequestAuthorizer` never checks it. The code
 * wins.)
 */
export function hasEventHubAccess(
  ldapGroups: readonly string[] | undefined,
): boolean {
  return rolesOf(ldapGroups).size > 0;
}

// ── Capabilities ─────────────────────────────────────────────────────────────
//
// Each predicate names one thing the UI can offer and maps to the roles the
// service's RouteRegistry allows for the operation behind it. Branch the UI on
// these rather than on a role: the role set can hold several roles at once, and a
// dual-role user should see the union of what they can do.

/** Holds a marketer role, either region. */
export function isMarketer(roles: ReadonlySet<EventHubRole>): boolean {
  return roles.has('MARKETER_CN') || roles.has('MARKETER_HK');
}

export function isBd(roles: ReadonlySet<EventHubRole>): boolean {
  return roles.has('BD');
}

export function isAdmin(roles: ReadonlySet<EventHubRole>): boolean {
  return roles.has('ADMIN');
}

/**
 * May create / edit / delete / publish an event.
 *
 * `POST /events`, `PATCH|DELETE /events/{eventId}`,
 * `POST /events/{eventId}/publish|unpublish` → MARKETER_CN, MARKETER_HK, ADMIN.
 * All five share one route gate. Delete is narrower inside the service — by creator and
 * state — so the UI offers it on {@link mayDeleteEvent}, not on this.
 */
export function canManageEvents(roles: ReadonlySet<EventHubRole>): boolean {
  return isMarketer(roles) || isAdmin(roles);
}

/**
 * Whether `login` is offered Delete on `event` — narrower than {@link canManageEvents}, because
 * the service's `EventService#delete` is:
 *
 *   admin       any event, in any state, PUBLISHED included
 *   marketer    only an event they created, and only while it is still a DRAFT
 *
 * "Created" is `createdBy`, compared exactly as the service does (`actor.equals(createdBy)`), not
 * the hand-over-able `eventOwner` (utils/eventOwnership): an owner who did not create the event
 * would be offered a button that 403s. A marketer gets nothing on an event with no `createdBy`,
 * since the service cannot establish ownership either. The route gate (`DELETE /events/{id}` →
 * MARKETERS_AND_ADMIN) is why a creator who has since lost the marketer role is not offered it.
 *
 * The campaign-hierarchy rule (no delete while a node references the event) is not here: it is
 * about the hierarchy, not the caller — see utils/eventDelete.
 */
export function mayDeleteEvent(
  event: { createdBy?: string; contentStatus?: string } | null | undefined,
  login: string | undefined,
  roles: ReadonlySet<EventHubRole>,
): boolean {
  if (!event) return false;
  if (isAdmin(roles)) return true;
  if (!isMarketer(roles)) return false;
  return !!login && event.createdBy === login && event.contentStatus === 'DRAFT';
}

/**
 * May approve or reject a nomination.
 *
 * `POST /nominations/{id}/approve|reject` → MARKETER_CN, MARKETER_HK, ADMIN. Those
 * operations are listed in the service's RouteRegistry but are NOT modeled yet, so
 * this currently gates UI with no backend behind it.
 */
export function canReviewNominations(roles: ReadonlySet<EventHubRole>): boolean {
  return isMarketer(roles) || isAdmin(roles);
}

/**
 * Whether `login` is offered Approve / Reject on `nomination` — the product rule:
 *
 *   marketer / admin   any nomination still PENDING_REVIEW, the event owner's included
 *   BD                 a PENDING_REVIEW one they did not submit — and since ListNominations gives
 *                      a BD only their own and their reports', that means a report's
 *
 * Nobody decides their own submission. Mirrors the service's `requireApprover` (a marketer or
 * admin, or a manager of the nomination's creator), so an offered button is one the service
 * accepts.
 */
export function mayDecideNomination(
  nomination: { status?: string; createdBy?: string },
  login: string | undefined,
  roles: ReadonlySet<EventHubRole>,
): boolean {
  if (nomination.status !== 'PENDING_REVIEW') return false;
  const me = login?.trim().toLowerCase();
  if (!me) return false;
  if (nomination.createdBy?.trim().toLowerCase() === me) return false;
  return isMarketer(roles) || isAdmin(roles) || isBd(roles);
}

/**
 * May nominate a guest, and look up nominatable contacts.
 *
 * `POST /nominations` and `GET /nomination-contacts` → BD, ADMIN. Note a marketer
 * may NOT nominate — the service excludes them, so the UI must too.
 */
export function canNominate(roles: ReadonlySet<EventHubRole>): boolean {
  return isBd(roles) || isAdmin(roles);
}

/**
 * May update a nominee's attendance.
 *
 * `PATCH /nominations/{id}/attendance` → BD, ADMIN. The service additionally
 * requires the nomination to be APPROVED and in the caller's ownership scope
 * (Layer 3); neither is knowable here, so a true result means "the role allows it",
 * not "this particular nomination allows it".
 */
export function canUpdateAttendance(roles: ReadonlySet<EventHubRole>): boolean {
  return isBd(roles) || isAdmin(roles);
}

/**
 * Sees only the events for their own BUs: a BD who cannot manage events (not also a marketer
 * or admin). The BD calendar, Upcoming Events and the event detail page all hide other BUs'
 * events from them. UX only — ListEvents / GetEvent still return those events to a BD today;
 * the authoritative restriction belongs in AWSECEventHub.
 */
export function seesOnlyOwnBuEvents(roles: ReadonlySet<EventHubRole>): boolean {
  return isBd(roles) && !canManageEvents(roles);
}

/** May impersonate another user on the Event Hub surface. */
export function canImpersonate(roles: ReadonlySet<EventHubRole>): boolean {
  return isAdmin(roles);
}

// ── Landing view ─────────────────────────────────────────────────────────────

/**
 * Which audience sections the landing page shows. A VIEW decision, deliberately separate from
 * the capability predicates: those answer "will the service allow this", this answers "whose
 * page is this".
 *
 * A marketer gets the marketer page and a BD the BD page; someone who is both gets both, which
 * is what they can actually do. ADMIN does not add a page of its own on top of an audience
 * role — a marketer who is also an admin sees the marketer page, not the BD one as well. Only an
 * admin with no audience role falls back to `adminDefault`, since they could otherwise see
 * either and both at once is noise.
 */
export function landingAudienceOf(
  roles: ReadonlySet<EventHubRole>,
  adminDefault: 'marketer' | 'bd',
): { marketer: boolean; bd: boolean } {
  const marketer = isMarketer(roles);
  const bd = isBd(roles);
  if (marketer || bd || !isAdmin(roles)) return { marketer, bd };
  return { marketer: adminDefault === 'marketer', bd: adminDefault === 'bd' };
}
